import {
  Injectable,
  Signal,
  WritableSignal,
  computed,
  signal,
} from '@angular/core';
import { IUser } from '@interface/user.interface';

export interface Asset {
  id: string;
  name: string;
  type: string;
  status?: 'MODIFIED' | 'ADDED' | 'REMOVED' | 'NO_CHANGE';
  parameters?: {
    FULLY_QUALIFIED_TABLE_NAME?: string;
    FQBN?: string;
    FQPN?: string;
  };
  schemas?: Asset[];
  tables?: Asset[];
  columns?: Asset[];
  expanded?: boolean;
  loading?: boolean;
  children?: Asset[];
  changeDetails?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ManageConnectionService<T extends IUser> {
  private state: WritableSignal<T> = signal({} as T);

  public set<K extends keyof T>(key: K, data: T[K]) {
    this.state.update((current) => ({ ...current, [key]: data }));
  }

  public setState(partial: Partial<T>): void {
    this.state.update((current) => ({ ...current, ...partial }));
  }

  public select<K extends keyof T>(key: K): Signal<T[K]> {
    return computed(() => this.state()[key]);
  }

  public transformHierarchyData(data: Asset[]): any[] {
    return data.map(item => {
      const transformed = {
        ...item,
        expanded: false,
        changeType: this.mapStatusToChangeType(item.status)
      };

      if (item.schemas) {
        transformed.children = this.transformHierarchyData(item.schemas);
      } else if (item.tables) {
        transformed.children = this.transformHierarchyData(item.tables);
      } else if (item.columns) {
        transformed.children = this.transformHierarchyData(item.columns);
      } else {
        transformed.children = item.children ? this.transformHierarchyData(item.children) : [];
      }

      return transformed;
    });
  }

  private mapStatusToChangeType(status?: string): string {
    switch (status) {
      case 'MODIFIED': return 'Updated';
      case 'ADDED': return 'Added';
      case 'REMOVED': return 'Deleted';
      default: return 'No Change';
    }
  }
}
